import * as ed from '@noble/ed25519';

const { bytesToHex, hexToBytes } = ed.etc;

export interface QrPayload {
	aid: string;
	eid: string;
	n: string;
	e: string | null;
	cc: string | null;
	iat: number;
}

function toB64Url(bytes: Uint8Array): string {
	let bin = '';
	for (const b of bytes) bin += String.fromCharCode(b);
	return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromB64Url(s: string): Uint8Array {
	const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
	const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
	const out = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
	return out;
}

const enc = new TextEncoder();
const dec = new TextDecoder();

export async function signQrToken(payload: QrPayload, privateKeyHex: string): Promise<string> {
	const body = toB64Url(enc.encode(JSON.stringify(payload)));
	const sig = await ed.signAsync(enc.encode(body), hexToBytes(privateKeyHex));
	return `${body}.${toB64Url(sig)}`;
}

export async function verifyQrToken(
	token: string,
	publicKeyHex: string
): Promise<{ valid: boolean; payload: QrPayload | null }> {
	try {
		const [body, sig] = token.split('.');
		if (!body || !sig) return { valid: false, payload: null };
		const ok = await ed.verifyAsync(fromB64Url(sig), enc.encode(body), hexToBytes(publicKeyHex));
		if (!ok) return { valid: false, payload: null };
		return { valid: true, payload: JSON.parse(dec.decode(fromB64Url(body))) as QrPayload };
	} catch {
		return { valid: false, payload: null };
	}
}

export async function generateEventKeypair(): Promise<{ privateKeyHex: string; publicKeyHex: string }> {
	const priv = ed.utils.randomSecretKey();
	const pub = await ed.getPublicKeyAsync(priv);
	return { privateKeyHex: bytesToHex(priv), publicKeyHex: bytesToHex(pub) };
}
